/**
 * `R3-UI5` · Carga en lote de participantes: el formulario que declara el
 * archivo y el lote, la lista de las cargas del brazo, y el reporte de una.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ENMIENDA `[29/9/2026]` · R3-UI5 · LO QUE ESTA PANTALLA DEJÓ DE SER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `AW17` escribió esta pantalla como la **pantalla INTERNA** de `ADR-0195`: un
 * formulario que además tenía un campo para pegar un `uploadId` y pedir su
 * reporte, y una lista de los `uploadId` de la sesión.
 *
 * R3 la cambia, y los dos cambios se ven en el código:
 *
 *   ① ⛔ **NO hay campo de identificador.** `R3-UI5` `CA-77` (regla 24, Fuera)
 *      retira el bloque «Pedir el reporte de una carga de este brazo»: el
 *      reporte se pide **desde la fila de la lista** (`CA-84`), y el operador no
 *      vuelve a ver un `uploadId`. Se va también la presentación de la pantalla
 *      como interna del equipo de Patagonian (`CA-77`).
 *   ② ⭐ **La lista sale del SERVIDOR, no de la sesión** (`CA-91`): `GET
 *      {brazo}/cargas`, con la más reciente arriba (`CA-93`), de ocho columnas,
 *      con los contadores con punto de miles (`CA-87`) y la fecha y la hora en
 *      hora de Chile (`CA-90`). Mientras alguna carga esté `procesando`, la lista
 *      se vuelve a pedir cada 5 s (`CA-85`) y esa fila no ofrece acción: sus
 *      números son «—» y su acción es ninguna (`CA-84`).
 *
 * ⇒ **Lo que la lista no guarda se ve al recargar**, y no hay nada que anotar:
 * eso es exactamente lo que `CA-91` gana. La versión vieja decía «estos
 * identificadores son la única forma de volver a pedir un reporte» — ya no hay
 * identificadores a la vista.
 *
 * ⭐ **El brazo sigue saliendo de la DIRECCIÓN**, nunca del formulario
 * (`R1.1-F4-CA-29`, `B25`): no hay campo, ni desplegable, ni `placeholder` que lo
 * sugiera. La carga se declara con **dos** campos, Archivo y Lote, y el brazo se
 * dice (`CA-76`), no se elige.
 *
 * ⛔ **De dónde sale el rechazo del archivo ENTERO: del SERVIDOR.** La pantalla
 * no inspecciona el archivo —no lo lee, no mira su encoding, no cuenta sus
 * filas—: manda los bytes y muestra el texto que el servidor devuelve, debajo del
 * campo Archivo (guard «El archivo lo valida el servidor»). Los cuatro textos
 * viven acá abajo porque es lo que la pantalla dibuja.
 *
 * ⚠️ **El `lote` va con tope de 100** y el botón se apaga sin archivo o con el
 * lote vacío —incluido un lote de SOLO espacios— (`CA-79`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ DE QUIÉN ES ESTA PANTALLA — `ADR-0195`, Y LO QUE ESE ADR **NO** LEVANTA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `ADR-0195` (`accepted`, 26/8/2026) decidió que la carga en lote por archivo es
 * **INTERNA del operador de Patagonian**: reemplaza la mitad de `P0-3` que hoy
 * manda cargar «por query directa a la base (sin pasar por el endpoint)», y **NO**
 * reemplaza la colección Postman de Elige, que `docs/prd/F8.md` declara
 * «entregable de Patagonian, no un accesorio: **es la interfaz real de carga del
 * cliente**».
 *
 * ⛔ **Y lo que ese ADR §Alcance NO levanta cae sobre esta pantalla en
 * particular, porque ES la pantalla:** «La pantalla interna **no se le entrega,
 * ni se le documenta, ni se le ofrece a Elige** como alternativa. Si Elige pide
 * cargar por pantalla, eso es **una pregunta nueva para el cliente**, no un
 * permiso que este ADR concede». El propio ADR declara que esa contención **es
 * prosa y no hay ningún mecanismo que la haga cumplir** — así que queda escrita
 * acá, y este archivo **no finge que el código la sostiene**.
 *
 * ⛔⛔ **La tensión con el PRD va escrita y son dos sitios**, los dos vigentes en
 * el disco: `docs/prd/F8.md` `P0-3` dice «**No hay pantalla de carga en el
 * Management Tool**», y §Notas técnicas dice «Cargas excepcionales por query del
 * equipo técnico, **sin UI de carga adicional**» —sin calificar, más ancho que el
 * primero—. `ADR-0195` §Consecuencias los deja como deuda **con dueño el TL**.
 *
 * ⚠️ **Y esta pantalla tiene una condición de vencimiento, con dueño.**
 * `docs/prd/open-questions.md` `Q17` sigue 🟡 abierta —«¿Quién opera el
 * Management Tool: Patagonian, Elige, o ambos?»— y `ADR-0195` lo dice sin
 * rodeos: «si la respuesta fuera “ambos”, la premisa de este ADR se debilita:
 * **una pantalla interna a la que entra el cliente ES una pantalla de carga para
 * el cliente**». **Dueño: TL.**
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ `lote` ES UN CAMPO QUE EL OPERADOR DECLARA, Y EL BRAZO NO ES UN CAMPO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `docs/prd/F8.md` `P0-1` (textual): «*Dado* que el nombre del archivo dice
 * cualquier cosa, *entonces* **el lote y el brazo se toman del request, no del
 * nombre del archivo**».
 *
 * ⛔ **Ni pre-llenado desde el nombre del archivo, ni sugerido como `placeholder`
 * a partir de él, ni «propuesto» de ninguna otra manera.** La comodidad que
 * cualquier revisor de UI va a pedir —el archivo se llama `<algo>-2026-08.csv`,
 * así que pre-llenar el lote con `2026-08` «le ahorra un campo al operador»—
 * tiene un modo de falla que **funciona casi siempre**: el operador confirma sin
 * leer, y el día que el archivo venga renombrado o de otra carpeta, **el lote
 * entero entra con el lote equivocado**, y el `202` se ve igual.
 *
 * ⇒ **este archivo no lee `archivo.name` para nada más que mostrarlo** — y ni eso:
 * ahora lo manda el servidor en la lista (`Carga.archivo`).
 *
 * ⛔ **Ni credenciales ni estado persistido**: no hay `localStorage`, no hay
 * `sessionStorage` y no hay cookie escrita desde acá. La lista se pide al
 * servidor cada vez (`CA-91`).
 */

import { useCallback, useEffect, useRef, useState } from "react"
import type { ChangeEvent, JSX } from "react"
import { useBrazo } from "../brazo.ts"
import { useConfiguracion } from "../contexto-configuracion.tsx"
import { esBrazo, losOtrosBrazos, selloDelBrazo } from "../zonas.ts"
import type { Brazo } from "../zonas.ts"
import type { Carga, EstadoDeCarga } from "@contracts/participants/index.ts"
import type { BordeCarga } from "./borde-carga.ts"
import { ReporteDeValidacion } from "./reporte.tsx"
import { conMiles, fechaHoraChile, OPCIONES_DE_FECHA } from "./formato.ts"

/**
 * ⭐ `CA-87` / `CA-90` · la lista formatea con `conMiles`/`fechaHoraChile`, de
 * `formato.ts`: ahí vive la CONVENCIÓN del formato, compartida con el panel del
 * reporte, así que una regla del PRD se arregla en un solo archivo y no en dos
 * copias que se desincronizan.
 *
 * Las INSTANCIAS de `Intl` sí se declaran acá, **una por pantalla y no una por
 * celda** (construir un `Intl.NumberFormat` es caro y esto se dibuja una vez por
 * fila, una vez por carga) y se le pasan al formateador compartido. Es la lista
 * la que elige el locale, y el gate de la lista exige ver el formateador en este
 * archivo.
 */
const MILES = new Intl.NumberFormat("es-CL")
const FECHA_Y_HORA = new Intl.DateTimeFormat("es-CL", OPCIONES_DE_FECHA)

/* ── Los cuatro rechazos del archivo ENTERO ────────────────────────────────
 *
 * ⭐ Salen del servidor (`cable-R3-UI5.md` §95), textuales: la pantalla no los
 * inventa, no los clasifica y **no inspecciona el archivo** para adivinar cuál
 * toca. Se muestran debajo del campo Archivo, que es el campo del que hablan.
 *
 * ⛔ Los cuatro son del archivo ENTERO y por eso ninguno deja la pantalla sin
 * lista: una fila rechazada **no** es un rechazo del archivo (`P0-2`: «sin frenar
 * el procesamiento de las válidas»), y va al reporte, no acá.
 */
const RECHAZO_NO_ES_UTF8 =
  "El archivo no está en UTF-8. Guárdalo como «CSV UTF-8» y vuelve a cargarlo."
const RECHAZO_SIN_COLUMNAS =
  "La primera fila del archivo tiene que tener las columnas «Nombre» y «Teléfono»."
const RECHAZO_SIN_FILAS = "El archivo no tiene filas para cargar."
const RECHAZO_DEMASIADAS_FILAS =
  "El archivo tiene más de 20.000 filas. Divídelo en archivos de hasta 20.000 filas."

const RECHAZOS_DEL_ARCHIVO: readonly string[] = [
  RECHAZO_NO_ES_UTF8,
  RECHAZO_SIN_COLUMNAS,
  RECHAZO_SIN_FILAS,
  RECHAZO_DEMASIADAS_FILAS,
]

/**
 * El texto que se muestra bajo el campo Archivo: el del servidor cuando es uno de
 * los cuatro conocidos, y el crudo cuando no — **un rechazo que no se entiende se
 * muestra igual**, porque taparlo dejaría al operador mirando un formulario que
 * no hace nada y no dice por qué.
 */
function mensajeDeRechazo(crudo: string): string {
  const conocido = RECHAZOS_DEL_ARCHIVO.find((texto) => crudo.includes(texto.slice(0, 24)))
  return conocido ?? crudo
}

/** Lo que el `202` NO devuelve y hay que capturar al enviar, para poder decirlo. */
type Aceptada = {
  readonly archivo: string
  readonly lote: string
}

function mensajeDe(causa: unknown): string {
  return causa instanceof Error ? causa.message : String(causa)
}

/**
 * `CA-79` · el botón se habilita con **un archivo elegido** y **un lote con
 * algo**, y no mientras hay un envío en curso.
 *
 * `.trim()` y no `lote === ""`: un lote de SOLO espacios pasaría por vacío y el
 * botón quedaría habilitado para mandar un pedido que el servidor rechaza.
 */
function puedeCargar(archivo: File | null, lote: string, cargando: boolean): boolean {
  return archivo !== null && lote.trim() !== "" && !cargando
}

/** El rótulo del estado que dibuja la columna «Estado» (`CA-84`). */
function rotuloDelEstado(estado: EstadoDeCarga): string {
  return estado === "lista" ? "Lista" : "Procesando"
}

/**
 * ⭐ `CA-84` · **sólo una carga terminada tiene reporte que mostrar**, así que
 * sólo su fila ofrece la acción. Es la condición que se lee en la columna
 * «Acciones» junto al botón.
 */
function esTerminada(estado: EstadoDeCarga): boolean {
  return estado === "lista"
}

/** ⭐ `CA-93` · «la más reciente arriba», como dice la leyenda de la lista. */
function masRecientePrimero(a: Carga, b: Carga): number {
  return b.aceptadaEn.getTime() - a.aceptadaEn.getTime()
}

export function PantallaCargaEnLote({ borde }: { readonly borde: BordeCarga }): JSX.Element {
  /**
   * ⭐ `CA-85` · **el sondeo de la lista late al compás de la instancia.** `refrescoMs`
   * es el mismo campo que `banco-de-contenido/tabla.tsx` usa como `refetchInterval`;
   * acá se lee del contexto y no de una constante, para que un examen pueda ejercer el
   * bucle sin esperar 5 s de reloj real (`pruebas/entorno.ts` espera de verdad).
   */
  const { refrescoMs } = useConfiguracion()

  /**
   * ⭐ `R1.1-F4-CA-29` / `B25` · **el brazo de la DIRECCIÓN**, estrechado a los dos
   * que existen. `null` ⇒ la carga no se ofrece, con el motivo escrito.
   */
  const brazoDeLaRuta = useBrazo()
  const brazo: Brazo | null = esBrazo(brazoDeLaRuta) ? brazoDeLaRuta : null
  const selloDelBrazoDeLaRuta = brazo === null ? "" : selloDelBrazo(brazo)

  const [archivo, setArchivo] = useState<File | null>(null)
  const [lote, setLote] = useState("")
  const [cargando, setCargando] = useState(false)
  const [aceptada, setAceptada] = useState<Aceptada | null>(null)
  const [errorDelArchivo, setErrorDelArchivo] = useState<string | null>(null)
  const [errorDeLaLista, setErrorDeLaLista] = useState<string | null>(null)
  const [cargas, setCargas] = useState<readonly Carga[]>([])
  const [cargaAbierta, setCargaAbierta] = useState<Carga | null>(null)

  const campoArchivo = useRef<HTMLInputElement>(null)

  /**
   * ⭐ `CA-91` · la lista **se pide al servidor**, y el pedido cuelga del brazo de
   * la dirección: `borde.listarCargas()` arma su ruta con `raizDelBrazo`. Acá no
   * se guarda nada entre recargas.
   */
  const refrescar = useCallback(() => {
    borde.listarCargas().then(
      (traidas) => {
        setCargas([...traidas].sort(masRecientePrimero))
        setErrorDeLaLista(null)
      },
      (causa: unknown) => {
        setErrorDeLaLista(mensajeDe(causa))
      },
    )
  }, [borde])

  /** El primer pedido de la lista, y cada vez que cambia el brazo del que cuelga. */
  useEffect(() => {
    void refrescar()
  }, [refrescar])

  const hayProcesando = cargas.some((carga) => carga.estado === "procesando")

  /**
   * ⭐ `CA-85` · **el sondeo dura lo que dura un procesamiento**: mientras haya
   * alguna carga procesando, la lista se vuelve a pedir cada `refrescoMs`;
   * cuando la última termina, el reloj se apaga. Sin la condición, la pantalla
   * pediría una lista que ya no cambia para siempre.
   *
   * ⚠️ **Los 5 s de la instancia se escriben ACÁ y no en una constante de módulo.**
   * El `5_000` es el default que `configuracion.ts` declara (`REFRESCO_MS_POR_DEFECTO`)
   * y queda al lado de la condición que lo gobierna (`hayProcesando`): separados, el
   * número queda huérfano arriba y la guarda invisible abajo.
   */
  useEffect(() => {
    if (!hayProcesando) return undefined
    const cada = refrescoMs > 0 ? refrescoMs : 5_000
    const reloj = setInterval(() => {
      void refrescar()
    }, cada)
    return () => clearInterval(reloj)
  }, [hayProcesando, refrescar, refrescoMs])

  const elegir = (evento: ChangeEvent<HTMLInputElement>): void => {
    setArchivo(evento.target.files?.[0] ?? null)
    setErrorDelArchivo(null)
    setAceptada(null)
  }

  const enviar = (): void => {
    /*
      ⛔ `archivo === null` se chequea ACÁ y no sólo dentro de `puedeCargar`: el
      booleano no estrecha el tipo, así que `enviado` quedaba `File | null` y
      `borde.cargar` —que pide un `File`— no compilaba. Es el mismo chequeo que
      `puedeCargar` hace adentro, repetido porque el `tsc` no lo puede ver.
    */
    if (brazo === null || archivo === null || !puedeCargar(archivo, lote, cargando)) return
    const enviado = archivo
    const loteDeclarado = lote.trim()
    setCargando(true)
    setErrorDelArchivo(null)
    borde.cargar({ archivo: enviado, lote: loteDeclarado, brazo }).then(
      () => {
        setCargando(false)
        setAceptada({ archivo: enviado.name, lote: loteDeclarado })
        /*
          `CA-89` · el formulario se vacía: el archivo elegido y el lote declarado
          se van, porque lo que queda es una carga ACEPTADA —y volver a mandar el
          mismo archivo por un formulario que se ve lleno es el error que esto
          evita—.
        */
        setArchivo(null)
        setLote("")
        if (campoArchivo.current !== null) campoArchivo.current.value = ""
        refrescar()
      },
      (causa: unknown) => {
        setCargando(false)
        /* El pedido NO llegó a ser una carga: no hay reporte ni fila, y el
           archivo elegido sigue donde estaba para poder reintentar. */
        setErrorDelArchivo(mensajeDeRechazo(mensajeDe(causa)))
      },
    )
  }

  /**
   * ⭐ `CA-95` / `CA-113` · el «otro brazo» del reporte **no viaja en la carga**:
   * `Carga` no lo trae, y se deriva del brazo de la dirección.
   */
  const otroDeLaRuta = brazo === null ? null : (losOtrosBrazos(brazo)[0] ?? null)
  const otroBrazo: Brazo | null = esBrazo(otroDeLaRuta) ? otroDeLaRuta : null

  return (
    <section className="carga-en-lote">
      {/*
        ⭐ `R3-UI5` · el encabezado con el título de la pantalla, como el resto del
        brazo. `r11-pantallas-del-brazo.test.tsx` lee `main h1` de cada pantalla y
        espera «Carga en lote»; el envoltorio `.carga-en-lote__encabezado` es el que
        `estilos.css:1925/1944/1957` declara junto a los otros seis del menú.
      */}
      <header className="carga-en-lote__encabezado">
        <h1>Carga en lote</h1>
      </header>

      {/*
        ⭐ `CA-89` (`r3-ui5.md:45`, `PRD_R3.md:2100`) · el aviso de aceptación va
        ARRIBA del formulario, no pegado al botón: describe lo que pasó con el
        envío anterior, y leerlo debajo del botón lo mezcla con la acción que se
        está por hacer. El formulario ya se vació al aceptar (ver `enviar`), así
        que el aviso habla del envío que terminó.
      */}
      {aceptada !== null && (
        <p className="carga-en-lote__estado-carga" role="status">
          Se aceptó «{aceptada.archivo}» para el lote «{aceptada.lote}». Su reporte queda en la
          lista cuando termine.
        </p>
      )}

      {/* ══ ① EL FORMULARIO ═══════════════════════════════════════════════ */}
      {/*
        ⚠️ `carga-en-lote__formulario` ADEMÁS de `__seccion`: `AW17` cuenta los
        campos del formulario por ese contenedor
        (`querySelectorAll(".carga-en-lote__formulario input")` — dos: Archivo y
        Lote, y ningún `<select>`), que es como el árbol lo venía nombrando. Las
        dos clases tienen su regla en `estilos.css:1150` y `:4118` (CA-122).
      */}
      <section className="carga-en-lote__seccion carga-en-lote__formulario">
        <h2 className="carga-en-lote__subtitulo">El archivo y lo que se declara de él</h2>

        {/*
          ⭐ `CA-76` · el aviso del brazo, con el NOMBRE VISIBLE interpolado y
          nunca escrito a mano: un literal «Brazo Híbrido» miente en el Puro.

          ⟦rastro · ADR-0082 · R3-UI5 · 29/9/2026⟧ Acá decía
          ~~`{selloDelBrazo(brazo)}`~~. El literal «…se cargan en KAI» lo exige el
          gate (`R3-UI5-formulario.gate.ts:98`) y `selloDelBrazo` **ya trae la
          instancia adelante** (`zonas.ts:244-246`), así que los dos juntos
          decían «KAI · KAI · Brazo Puro» y `AW17` › `CA-30` se ponía rojo. Se
          interpola `nombreDelBrazo` —«Brazo Puro», sin instancia— y la instancia
          la pone el texto fijo, que es como `padron/tabla.tsx` lo venía haciendo.

          ⛔ La carga no se ofrece sin un brazo válido en la dirección: sin él no
          se sabe a dónde van los participantes. Se dice por qué y el botón queda
          apagado, en vez de ofrecer un envío que el servidor no puede resolver.
        */}
        {brazo === null ? (
          <p className="carga-en-lote__nota" role="status">
            <strong>No se puede cargar el archivo.</strong> La dirección no declara ningún brazo
            válido, y la carga tiene que decir a qué brazo van los participantes. Entra por el menú
            del brazo.
          </p>
        ) : (
          /*
            ⟦rastro · ADR-0082 · ADR-0402 · 30/9/2026 · R3-H-005 · CP-R3-F5-076⟧ **EL RASTRO DE
            ARRIBA QUEDA, Y ESTO LO REVIERTE.** Volvió `{selloDelBrazo(brazo)}` y el texto fijo
            dejó de traer «KAI».

            El «KAI · KAI · Brazo Puro» que cuenta el rastro anterior era real, pero la causa no
            era `selloDelBrazo`: era que el literal y el sello traían los dos la instancia. Se
            sacó el que NO correspondía. `R2-E3` (`PRD_R2-errata.md:60-66`, invariante `I-11`)
            superó el literal el 23-24/9/2026 — «la consola antepone el nombre de la instancia
            declarado en su configuración, sea cual sea» —, así que hornear «KAI» mostraba «KAI»
            en una instancia llamada «KAI (ambiente de QA)».

            Y el gate que exigía el literal (`R3-UI5-formulario.gate.ts`) **era el que imponía el
            defecto**: es un juez de FUENTE, así que cualquier parametrización correcta le hacía
            desaparecer la cadena. Se corrigió en el mismo cierre y ahora PROHÍBE «KAI» acá.
          */
          <p className="carga-en-lote__nota">
            Los participantes se cargan en {selloDelBrazo(brazo)}. El brazo nunca se toma del
            archivo.
          </p>
        )}

        {/* ── El archivo ─────────────────────────────────────────────────── */}
        <div className="carga-en-lote__campo">
          <label htmlFor="carga-de-archivo">Elegir archivo</label>
          <input
            id="carga-de-archivo"
            ref={campoArchivo}
            type="file"
            accept=".csv,text/csv"
            onChange={elegir}
          />
          {/*
            ⭐ El archivo elegido se dice en pantalla: si el pedido no llega, el
            operador tiene que VER que su archivo sigue elegido para reintentar
            sin volver a buscarlo (`AW17` · «si el pedido no llega, se dice y el
            archivo elegido sigue elegido»). Se dice el NOMBRE; el lote y el
            brazo siguen sin salir de acá (P0-1).
          */}
          {archivo !== null && (
            <p className="carga-en-lote__ayuda">Archivo elegido: {archivo.name}</p>
          )}
          {/*
            ⭐ `CA-78` · la ayuda del formato. ⛔ El caso lo decide el SERVIDOR: la
            pantalla no lee el archivo para saber si es UTF-8 ni cuántas filas
            trae — manda los bytes y muestra el texto del rechazo.
          */}
          <p className="carga-en-lote__ayuda">
            CSV en UTF-8 con las columnas «Nombre» y «Teléfono». Teléfonos de cualquier país, con
            su código de país. Hasta 20.000 filas.
          </p>
          {/*
            ⛔ El rechazo del archivo ENTERO, DEBAJO del campo del que habla: es
            el archivo entero el que no entró, no una fila. Una fila rechazada no
            aparece acá: va al reporte de la carga.
          */}
          {errorDelArchivo !== null && (
            <p className="carga-en-lote__error" role="alert">
              {mensajeDeRechazo(errorDelArchivo)}
            </p>
          )}
        </div>

        {/* ── El lote ────────────────────────────────────────────────────── */}
        <div className="carga-en-lote__campo">
          <label htmlFor="lote-de-la-carga">Lote</label>
          <input
            id="lote-de-la-carga"
            type="text"
            /*
              ⚠️ El tope lo pone el campo y no una validación de la pantalla: el
              servidor rechaza un lote demasiado largo, y dejar escribirlo para
              después rechazarlo es hacer perder el trabajo dos veces.
            */
            maxLength={100}
            value={lote}
            onChange={(evento) => setLote(evento.target.value)}
          />
          <p className="carga-en-lote__ayuda">
            El lote se toma de lo que se declara aquí, nunca del nombre del archivo.
          </p>
        </div>

        <button
          type="button"
          className="boton boton--primario"
          disabled={brazo === null || !puedeCargar(archivo, lote, cargando)}
          onClick={() => {
            void enviar()
          }}
        >
          {cargando ? "Cargando…" : "Cargar el archivo"}
        </button>
      </section>

      {/* ══ ② LA LISTA DE LAS CARGAS DEL BRAZO ════════════════════════════ */}
      <section className="carga-en-lote__seccion">
        <h2 className="carga-en-lote__subtitulo">
          Cargas de {selloDelBrazoDeLaRuta}, la más reciente arriba.
        </h2>

        {errorDeLaLista !== null && (
          <p className="carga-en-lote__error" role="alert">
            No se pudo leer la lista de cargas: {errorDeLaLista}
          </p>
        )}

        {cargas.length === 0 ? (
          <p className="carga-en-lote__vacio">
            Todavía no hay cargas en {selloDelBrazoDeLaRuta}.
          </p>
        ) : (
          <div className="carga-en-lote__tabla-rodante">
            <table className="carga-en-lote__tabla">
              <thead>
                <tr>
                  <th scope="col">Fecha y hora</th>
                  <th scope="col">Lote</th>
                  <th scope="col">Archivo</th>
                  <th scope="col">Cargó</th>
                  <th scope="col">Filas cargadas</th>
                  <th scope="col">Filas rechazadas</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cargas.map((fila) => (
                  <tr key={fila.id}>
                    <td>{fechaHoraChile(fila.aceptadaEn, FECHA_Y_HORA)}</td>
                    <td>{fila.lote}</td>
                    <td>{fila.archivo}</td>
                    {/*
                      ⭐ `CA-90` · la columna dice QUIÉN cargó —el nombre que el
                      servidor resolvió—, no su correo ni el identificador de la
                      carga: un `uploadId` no le dice nada a quien opera.
                    */}
                    <td>{fila.actor}</td>
                    {/*
                      ⛔ `CA-84` · mientras procesa, los contadores son «—»: un
                      `0` se lee como «no cargó ninguna», que es una afirmación
                      que la pantalla todavía no puede hacer.
                    */}
                    <td>{esTerminada(fila.estado) ? conMiles(fila.cargadas, MILES) : "—"}</td>
                    <td>{esTerminada(fila.estado) ? conMiles(fila.rechazadas, MILES) : "—"}</td>
                    <td>{rotuloDelEstado(fila.estado)}</td>
                    <td>
                      {/*
                        ⭐ `CA-84` · la acción sólo en la fila TERMINADA: una
                        carga que todavía procesa no tiene reporte que mostrar, y
                        ofrecerlo llevaría a una pantalla que no puede contestar.
                      */}
                      {esTerminada(fila.estado) ? (
                        <button
                          type="button"
                          className="boton boton--chico"
                          onClick={() => setCargaAbierta(fila)}
                        >
                          Ver reporte
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/*
        ⭐ `CA-99` · el reporte es un PANEL sobre la lista, no otra pantalla: la
        dirección no cambia y el operador vuelve a la misma lista cerrando con
        Escape o con el botón. Lo dibuja `ReporteDeValidacion`, que se cierra solo
        si no hay carga abierta.
      */}
      {/*
        ⟲ 30/9/2026 · L2 (revisión de `fix/consola-literales-de-brazo`): era
        ~~`otroBrazo !== null && brazo !== null && …`~~, y en `OLI` (un brazo)
        `otroBrazo` es `null`: «Ver reporte» no abría NADA. El reporte depende del
        brazo propio; el otro sólo decide si se dibuja el contador cruzado.
      */}
      {brazo !== null && (
        <ReporteDeValidacion
          carga={cargaAbierta}
          brazo={brazo}
          otroBrazo={otroBrazo}
          onCerrar={() => setCargaAbierta(null)}
        />
      )}
    </section>
  )
}
